import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { 
  TrendingUp, DollarSign, ShoppingBag, Copy, Check, 
  ExternalLink, Clock, Gift, ChevronRight, Zap,
  BarChart3, Users, Percent, Image as ImageIcon, FileText, X,
  Wallet, AlertCircle, MousePointerClick, UserPlus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Pure SVG line chart for affiliate monthly commission evolution.
 * Zero dependency. Smooth cubic curve, area fill, dots on data points,
 * +X% growth badge top-right, interactive HTML tooltip on hover/touch.
 */
const EvolutionChart = ({ history }) => {
  const [hoverIdx, setHoverIdx] = useState(null);
  const wrapRef = useRef(null);

  if (!history || history.length === 0) return null;

  const W = 640;
  const H = 240;
  const PAD_L = 44;
  const PAD_R = 16;
  const PAD_TOP = 24;
  const PAD_BOTTOM = 32;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_TOP - PAD_BOTTOM;

  const values = history.map((m) => Number(m.commission) || 0);
  const rawMax = Math.max(...values, 1);
  // Round up to "nice" max for Y axis (e.g., 1.9 → 2, 7 → 10, 28 → 30)
  const niceCeil = (n) => {
    if (n <= 1) return 1;
    if (n <= 2) return 2;
    if (n <= 5) return 5;
    const pow = Math.pow(10, Math.floor(Math.log10(n)));
    const r = n / pow;
    if (r <= 1) return pow;
    if (r <= 2) return 2 * pow;
    if (r <= 5) return 5 * pow;
    return 10 * pow;
  };
  const max = niceCeil(rawMax);
  const n = values.length;

  const points = values.map((v, i) => {
    const x = PAD_L + (n === 1 ? innerW / 2 : (i * innerW) / (n - 1));
    const y = PAD_TOP + innerH - (v / max) * innerH;
    return { x, y, v };
  });

  const linePath = points.reduce((acc, p, i, arr) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    const prev = arr[i - 1];
    const cpx = (prev.x + p.x) / 2;
    return `${acc} C ${cpx} ${prev.y}, ${cpx} ${p.y}, ${p.x} ${p.y}`;
  }, '');
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${PAD_TOP + innerH} L ${points[0].x} ${PAD_TOP + innerH} Z`;

  const firstNonZero = values.find((v) => v > 0) || 0;
  const last = values[values.length - 1] || 0;
  let growth = null;
  if (firstNonZero > 0) growth = ((last - firstNonZero) / firstNonZero) * 100;
  else if (last > 0) growth = 100;
  const growthLabel = growth === null ? null : `${growth >= 0 ? '+' : ''}${Math.round(growth)}%`;

  // Y axis ticks (4 levels)
  const yTicks = [0, max / 4, max / 2, (3 * max) / 4, max];

  // Hit-test rectangles (one per data point) for hover/touch
  const segW = innerW / Math.max(n - 1, 1);

  return (
    <div
      data-testid="affiliate-evolution-chart"
      className="relative bg-gradient-to-b from-zinc-900/60 to-zinc-950/80 border border-purple-900/30 rounded-2xl p-4 md:p-6 mb-4 md:mb-6 overflow-hidden"
    >
      {/* Decorative blur */}
      <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-purple-500/10 blur-3xl pointer-events-none" />

      {/* Header */}
      <div className="flex items-start justify-between mb-3 relative">
        <div>
          <h3 className="text-zinc-300 text-[11px] md:text-xs font-semibold uppercase tracking-[0.18em]">
            Évolution des gains (6 mois)
          </h3>
          <p className="text-gray-500 text-[11px] md:text-xs mt-0.5">Survole les points pour voir le détail</p>
        </div>
        {growthLabel && (
          <span className={`px-2.5 py-1 rounded-lg text-xs md:text-sm font-bold tabular-nums border ${
            growth >= 0
              ? 'bg-purple-500/15 border-purple-500/40 text-purple-200'
              : 'bg-red-500/15 border-red-500/40 text-red-300'
          }`}>
            {growthLabel}
          </span>
        )}
      </div>

      {/* Chart wrapper */}
      <div ref={wrapRef} className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="w-full h-48 md:h-64 block"
        >
          <defs>
            <linearGradient id="affChartGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#a855f7" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="affLineGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#8b5cf6" />
              <stop offset="100%" stopColor="#d946ef" />
            </linearGradient>
            <filter id="affGlow">
              <feGaussianBlur stdDeviation="3" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Horizontal gridlines + Y labels */}
          {yTicks.map((t, i) => {
            const y = PAD_TOP + innerH - (t / max) * innerH;
            return (
              <g key={`g-${i}`}>
                <line
                  x1={PAD_L} x2={W - PAD_R} y1={y} y2={y}
                  stroke="#3f3f46" strokeOpacity={i === 0 ? 0.45 : 0.18}
                  strokeDasharray={i === 0 ? '0' : '3 4'}
                />
                <text
                  x={PAD_L - 8} y={y + 4}
                  textAnchor="end"
                  fontSize="10"
                  fill="#71717a"
                >
                  {t.toFixed(t < 10 ? 1 : 0)}€
                </text>
              </g>
            );
          })}

          {/* Area */}
          <path d={areaPath} fill="url(#affChartGrad)" />

          {/* Line */}
          <path
            d={linePath}
            fill="none"
            stroke="url(#affLineGrad)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#affGlow)"
          />

          {/* Vertical guide on hover */}
          {hoverIdx !== null && points[hoverIdx] && (
            <line
              x1={points[hoverIdx].x} x2={points[hoverIdx].x}
              y1={PAD_TOP} y2={PAD_TOP + innerH}
              stroke="#a855f7" strokeOpacity="0.45" strokeDasharray="3 3"
            />
          )}

          {/* Dots */}
          {points.map((p, i) => {
            const active = hoverIdx === i;
            return (
              <g key={`d-${i}`}>
                <circle
                  cx={p.x} cy={p.y}
                  r={active ? 6 : 4}
                  fill={active ? '#a855f7' : '#0a0b14'}
                  stroke="#c084fc"
                  strokeWidth="2"
                  style={{ transition: 'r 150ms ease, fill 150ms ease' }}
                />
              </g>
            );
          })}

          {/* X labels (months) */}
          {points.map((p, i) => (
            <text
              key={`l-${i}`}
              x={p.x}
              y={H - 8}
              textAnchor="middle"
              fontSize="11"
              fill={hoverIdx === i ? '#d8b4fe' : '#9ca3af'}
              style={{ transition: 'fill 150ms' }}
            >
              {history[i].label}
            </text>
          ))}

          {/* Invisible hit zones for hover/touch */}
          {points.map((p, i) => (
            <rect
              key={`hit-${i}`}
              x={p.x - segW / 2} y={0}
              width={segW} height={H}
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
              onTouchStart={() => setHoverIdx(i)}
            />
          ))}
        </svg>

        {/* HTML Tooltip (positioned in % so it follows the responsive SVG) */}
        {hoverIdx !== null && points[hoverIdx] && (() => {
          const p = points[hoverIdx];
          const h = history[hoverIdx];
          const leftPct = (p.x / W) * 100;
          const topPct = (p.y / H) * 100;
          // Flip horizontally if too close to edges
          const flipRight = leftPct > 75;
          return (
            <div
              className="absolute pointer-events-none z-10 transition-opacity duration-150"
              style={{
                left: `${leftPct}%`,
                top: `${topPct}%`,
                transform: `translate(${flipRight ? 'calc(-100% - 12px)' : '12px'}, -50%)`,
              }}
            >
              <div className="bg-[#0d0f1e]/95 backdrop-blur-sm border border-purple-500/40 rounded-lg shadow-[0_10px_30px_-6px_rgba(168,85,247,0.5)] px-3 py-2 min-w-[120px]">
                <p className="text-[10px] uppercase tracking-wider text-purple-300 font-semibold">
                  {h.label} {h.year}
                </p>
                <p className="text-white text-base font-bold tabular-nums mt-0.5">
                  {p.v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                </p>
                <p className="text-gray-400 text-[11px] mt-0.5">
                  {h.sales} vente{h.sales > 1 ? 's' : ''}
                </p>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Total of the 6 months */}
      <div className="mt-3 flex items-center justify-between text-[11px] md:text-xs relative">
        <span className="text-gray-500">Total 6 derniers mois</span>
        <span className="text-purple-300 font-semibold tabular-nums">
          {values.reduce((a, b) => a + b, 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
        </span>
      </div>
    </div>
  );
};

/**
 * PayPal card — affiliate saves their PayPal (email or @handle) for payouts.
 */
const PayPalCard = ({ stats, token, onSuccess }) => {
  const [value, setValue] = useState(stats?.paypal || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setValue(stats?.paypal || ''); }, [stats?.paypal]);

  const isValid = (v) => /^\S+@\S+\.\S+$/.test(v) || /^@[A-Za-z0-9._-]{2,50}$/.test(v);

  const save = async () => {
    const v = value.trim();
    if (v && !isValid(v)) {
      setError('Format invalide : email PayPal ou @pseudo (ex: @MonPseudo)');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/my-affiliate/paypal`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ paypal: v }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Erreur lors de la sauvegarde.');
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (onSuccess) onSuccess();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="affiliate-paypal-card" className="bg-zinc-900/50 border border-blue-900/30 rounded-2xl p-4 md:p-6 mb-4 md:mb-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
          <Wallet className="w-4 h-4 md:w-5 md:h-5 text-blue-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-sm md:text-base">Mon PayPal</h3>
          <p className="text-gray-400 text-[11px] md:text-xs">Adresse email ou @pseudo PayPal — utilisé pour recevoir tes paiements</p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="email@exemple.com ou @MonPseudo"
          data-testid="affiliate-paypal-input"
          className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500 placeholder:text-gray-600"
        />
        <button
          onClick={save}
          disabled={saving || value.trim() === (stats?.paypal || '')}
          data-testid="affiliate-paypal-save-btn"
          className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
        >
          {saving ? <Clock className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : null}
          {saved ? 'Enregistré' : 'Enregistrer'}
        </button>
      </div>
      {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
    </div>
  );
};

/**
 * Payout claim card — affiliate requests a manual PayPal payment.
 * Requires pending ≥ 20€ AND no other pending request already.
 */
const PayoutClaimCard = ({ stats, token, onSuccess }) => {
  const [paypalEmail, setPaypalEmail] = useState(stats?.paypal || '');

  useEffect(() => { if (stats?.paypal) setPaypalEmail((prev) => prev || stats.paypal); }, [stats?.paypal]);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const pending = Number(stats?.pending_commission || 0);
  const MIN = Number(stats?.min_payout_amount || 20);
  const existing = stats?.pending_payout_request;
  const canClaim = pending >= MIN && !existing;
  const progress = Math.min(100, Math.round((pending / MIN) * 100));

  const submit = async () => {
    const v = paypalEmail.trim();
    if (!v || !(/^\S+@\S+\.\S+$/.test(v) || /^@[A-Za-z0-9._-]{2,50}$/.test(v))) {
      setError('PayPal invalide : email ou @pseudo (ex: @MonPseudo).');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/my-affiliate/request-payout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ paypal_email: paypalEmail.trim(), note: note.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Erreur lors de la demande.');
      }
      setSuccess(true);
      if (onSuccess) onSuccess();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Pending request already exists → show status
  if (existing) {
    return (
      <div data-testid="payout-pending-card" className="bg-gradient-to-r from-amber-950/40 via-zinc-900/60 to-zinc-900/60 border border-amber-500/30 rounded-2xl p-4 md:p-6 mb-4 md:mb-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
            <Clock className="w-5 h-5 md:w-6 md:h-6 text-amber-300" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-semibold text-base md:text-lg">Demande en cours de traitement</h3>
            <p className="text-gray-400 text-sm mt-1">
              Tu as demandé un paiement de <strong className="text-amber-300 tabular-nums">{Number(existing.amount).toFixed(2)} €</strong> sur <strong className="text-white">{existing.paypal_email}</strong>.
            </p>
            <p className="text-gray-500 text-xs mt-2">
              Envoyée le {new Date(existing.requested_at).toLocaleDateString('fr-FR')} · L'équipe te paie sous 7 jours.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-2xl p-4 md:p-6 mb-4 md:mb-6 flex items-start gap-3">
        <Check className="w-6 h-6 text-emerald-300 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-white font-semibold">Demande envoyée !</p>
          <p className="text-gray-400 text-sm mt-1">L'équipe traite ta demande sous 7 jours.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="payout-claim-card" className="relative bg-gradient-to-b from-zinc-900/60 to-zinc-950/80 border border-purple-900/30 rounded-2xl p-4 md:p-6 mb-4 md:mb-6 overflow-hidden">
      <div className="absolute -top-16 -left-16 w-48 h-48 rounded-full bg-purple-500/10 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
              <Wallet className="w-5 h-5 md:w-6 md:h-6 text-purple-300" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-base md:text-lg">Réclamer un paiement</h3>
              <p className="text-gray-400 text-xs md:text-sm">Versement PayPal — minimum {MIN.toFixed(2)} €</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-gray-500">Disponible</p>
            <p className={`text-xl md:text-2xl font-bold tabular-nums ${canClaim ? 'text-purple-200' : 'text-orange-300'}`}>
              {pending.toFixed(2)} €
            </p>
          </div>
        </div>

        {/* Progress bar to MIN */}
        {!canClaim && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-[11px] mb-1.5">
              <span className="text-gray-500">Il manque <strong className="text-orange-300">{(MIN - pending).toFixed(2)} €</strong></span>
              <span className="text-gray-500 tabular-nums">{progress}%</span>
            </div>
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-orange-500 to-purple-500 transition-all duration-700"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Claim form */}
        {canClaim && (
          <div className="mt-4">
            {!expanded ? (
              <button
                onClick={() => setExpanded(true)}
                data-testid="payout-claim-btn"
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-400 hover:to-fuchsia-400 text-white font-semibold rounded-xl transition-all shadow-[0_10px_30px_-8px_rgba(168,85,247,0.55)] hover:shadow-[0_14px_40px_-6px_rgba(168,85,247,0.75)]"
              >
                <Wallet className="w-4 h-4" />
                Réclamer {pending.toFixed(2)} €
              </button>
            ) : (
              <div className="space-y-3 bg-black/30 border border-purple-500/20 rounded-xl p-3 md:p-4 animate-[fadeIn_0.3s_ease-out]">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Email PayPal *</label>
                  <input
                    type="email"
                    value={paypalEmail}
                    onChange={(e) => setPaypalEmail(e.target.value)}
                    placeholder="ton.email@exemple.com"
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-purple-500/60 focus:bg-black/60 transition-all"
                    data-testid="payout-paypal-input"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Note (optionnel)</label>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Référence ou message pour l'équipe"
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-purple-500/60 focus:bg-black/60 transition-all"
                  />
                </div>
                {error && (
                  <p className="flex items-center gap-1.5 text-red-400 text-xs">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {error}
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => setExpanded(false)}
                    className="px-4 py-2.5 text-gray-400 hover:text-white text-sm font-medium transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={submit}
                    disabled={submitting}
                    data-testid="payout-submit-btn"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-400 hover:to-fuchsia-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-all"
                  >
                    {submitting ? 'Envoi...' : `Confirmer ${pending.toFixed(2)} €`}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const MyAffiliatePage = () => {
  const { isAuthenticated, token, user, loginWithDiscord } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoCopied, setPromoCopied] = useState(false);

  const requestPromo = async () => {
    setPromoBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/my-affiliate/request-promo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchStats();
      } else {
        const d = await res.json().catch(() => ({}));
        alert(d.detail || 'Erreur lors de la demande.');
      }
    } catch {
      alert('Erreur réseau.');
    } finally {
      setPromoBusy(false);
    }
  };

  const copyPromo = () => {
    if (stats?.promo_code) {
      navigator.clipboard.writeText(stats.promo_code);
      setPromoCopied(true);
      setTimeout(() => setPromoCopied(false), 2000);
    }
  };
  const [proofViewer, setProofViewer] = useState(null); // { id, image_base64, content_type, note }
  const [proofLoadingId, setProofLoadingId] = useState(null);

  const openProof = async (proofId) => {
    if (!proofId) return;
    setProofLoadingId(proofId);
    try {
      const res = await fetch(`${API_URL}/api/payment-proof/${proofId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProofViewer(data);
      } else {
        alert("Impossible de charger la preuve");
      }
    } catch (e) {
      console.error(e);
      alert("Erreur lors du chargement de la preuve");
    } finally {
      setProofLoadingId(null);
    }
  };

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchStats();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/my-affiliate-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
        setError(null);
      } else if (response.status === 403) {
        setError('not_affiliate');
      } else {
        setError('fetch_error');
      }
    } catch (err) {
      console.error('Error fetching affiliate stats:', err);
      setError('fetch_error');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (stats?.code) {
      navigator.clipboard.writeText(stats.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyLink = () => {
    if (stats?.code) {
      navigator.clipboard.writeText(`${window.location.origin}/${stats.code}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-20 pb-12 px-4">
        <div className="max-w-lg mx-auto text-center">
          <div className="w-20 h-20 bg-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Users className="w-10 h-10 text-purple-400" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">Espace Affilié</h1>
          <p className="text-gray-400 mb-8">Connecte-toi pour accéder à ton espace affilié</p>
          <button
            onClick={loginWithDiscord}
            className="px-6 py-3 bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold rounded-xl transition-colors flex items-center gap-2 mx-auto"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
            </svg>
            Se connecter avec Discord
          </button>
        </div>
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-12 px-4 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Not an affiliate
  if (error === 'not_affiliate') {
    return (
      <div className="min-h-screen pt-20 pb-12 px-4">
        <div className="max-w-lg mx-auto text-center">
          <div className="w-20 h-20 bg-zinc-800 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Users className="w-10 h-10 text-gray-500" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">Accès Réservé</h1>
          <p className="text-gray-400 mb-6">
            Tu n'as pas encore accès au programme affilié.
          </p>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-left">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-400" />
              Comment devenir affilié ?
            </h3>
            <ul className="text-gray-400 text-sm space-y-2">
              <li className="flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                Contacte-nous sur Discord pour faire ta demande
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                Partage ton audience (stream, réseaux sociaux, etc.)
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                Reçois ton code unique et gagne des commissions !
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen pt-20 pb-12 px-4">
        <div className="max-w-lg mx-auto text-center">
          <p className="text-red-400">Erreur lors du chargement des données</p>
          <button onClick={fetchStats} className="mt-4 text-cyan-400 hover:underline">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 px-4 md:px-8">
      <div className="max-w-5xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Espace Affilié</h1>
              <p className="text-gray-400 text-sm">Bienvenue, {user?.username}</p>
            </div>
          </div>
        </div>

        {/* Code & Link Section */}
        <div className="bg-gradient-to-br from-purple-950/40 to-pink-950/20 border border-purple-500/30 rounded-2xl p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <div className="flex-1">
              <p className="text-purple-300 text-sm font-medium mb-1">Ton code affilié</p>
              <div className="flex items-center gap-3">
                <code className="text-2xl md:text-3xl font-bold text-white tracking-wider">
                  {stats?.code}
                </code>
                <button
                  onClick={copyCode}
                  className="p-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg transition-colors"
                  title="Copier le code"
                >
                  {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5 text-purple-400" />}
                </button>
              </div>
              <p className="text-gray-400 text-xs mt-2">
                Commission: <span className="text-purple-400 font-semibold">{stats?.commission_rate}%</span> par vente
              </p>
            </div>
            
            <div className="flex-1">
              <p className="text-purple-300 text-sm font-medium mb-1">Ton lien de partage</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-black/30 rounded-lg px-3 py-2 text-gray-300 text-sm truncate">
                  {window.location.origin}/{stats?.code}
                </div>
                <button
                  onClick={copyLink}
                  className="p-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg transition-colors flex-shrink-0"
                  title="Copier le lien"
                >
                  <ExternalLink className="w-5 h-5 text-purple-400" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* === Code promo affilié === */}
        <div data-testid="affiliate-promo-card" className="bg-gradient-to-br from-fuchsia-950/30 to-purple-950/20 border border-fuchsia-500/30 rounded-2xl p-5 md:p-6 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-fuchsia-500/15 border border-fuchsia-500/30 flex items-center justify-center flex-shrink-0">
              <Percent className="w-5 h-5 text-fuchsia-300" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm md:text-base">Ton code promo</h3>
              <p className="text-gray-400 text-[11px] md:text-xs">Tes ventes te sont attribuées même sans passer par ton lien</p>
            </div>
          </div>

          {stats?.promo_code ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-2">
                <code className="text-xl md:text-2xl font-bold text-white tracking-wider bg-black/30 px-3 py-1.5 rounded-lg">
                  {stats.promo_code}
                </code>
                <button
                  onClick={copyPromo}
                  data-testid="affiliate-promo-copy-btn"
                  className="p-2 bg-fuchsia-500/20 hover:bg-fuchsia-500/30 rounded-lg transition-colors"
                  title="Copier le code promo"
                >
                  {promoCopied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5 text-fuchsia-300" />}
                </button>
              </div>
              <p className="text-gray-300 text-sm">
                Offre <span className="text-fuchsia-300 font-semibold">-{stats.promo_discount_percent || 5}%</span> à ta communauté 🎁
              </p>
            </div>
          ) : stats?.promo_requested ? (
            <div className="flex items-center gap-2 text-amber-300 text-sm bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3">
              <Clock className="w-4 h-4 flex-shrink-0" />
              Demande envoyée — en attente de validation par l'équipe.
            </div>
          ) : (
            <div>
              <p className="text-gray-400 text-sm mb-3">
                Demande un code promo perso : tes abonnés obtiennent une réduction, et <strong className="text-white">toutes les ventes avec ton code te sont créditées</strong> (même sans ton lien).
              </p>
              <button
                onClick={requestPromo}
                disabled={promoBusy}
                data-testid="affiliate-request-promo-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-fuchsia-500 to-purple-500 hover:from-fuchsia-400 hover:to-purple-400 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-all"
              >
                {promoBusy ? <Clock className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                Demander un code promo
              </button>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8">
          {/* Total Sales */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 md:p-5">
            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <ShoppingBag className="w-4 h-4 md:w-5 md:h-5 text-cyan-400" />
              </div>
              <span className="text-gray-400 text-xs md:text-sm font-medium">Ventes Totales</span>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-white">{stats?.total_sales || 0}</p>
            <p className="text-gray-500 text-[10px] md:text-xs mt-1">commandes validées</p>
          </div>

          {/* Total Commission */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 md:p-5">
            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-4 h-4 md:w-5 md:h-5 text-green-400" />
              </div>
              <span className="text-gray-400 text-xs md:text-sm font-medium">Commission Totale</span>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-green-400">{(stats?.total_commission || 0).toFixed(2)}€</p>
            <p className="text-gray-500 text-[10px] md:text-xs mt-1">gains cumulés</p>
          </div>

          {/* Pending Commission */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 md:p-5">
            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 md:w-5 md:h-5 text-orange-400" />
              </div>
              <span className="text-gray-400 text-xs md:text-sm font-medium">En Attente</span>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-orange-400">{(stats?.pending_commission || 0).toFixed(2)}€</p>
            <p className="text-gray-500 text-[10px] md:text-xs mt-1">à récupérer</p>
          </div>
        </div>

        {/* Trafic via ton lien : clics + connexions (arrivées Discord) */}
        <div className="grid grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 md:p-5" data-testid="affiliate-my-clicks-card">
            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <MousePointerClick className="w-4 h-4 md:w-5 md:h-5 text-cyan-400" />
              </div>
              <span className="text-gray-400 text-xs md:text-sm font-medium">Clics sur ton lien</span>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-cyan-400">{stats?.clicks || 0}</p>
            <p className="text-gray-500 text-[10px] md:text-xs mt-1">visites depuis ton lien</p>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 md:p-5" data-testid="affiliate-my-signups-card">
            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <UserPlus className="w-4 h-4 md:w-5 md:h-5 text-emerald-400" />
              </div>
              <span className="text-gray-400 text-xs md:text-sm font-medium">Connexions Discord</span>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-emerald-400">{stats?.signups || 0}</p>
            <p className="text-gray-500 text-[10px] md:text-xs mt-1">personnes venues via ton lien</p>
          </div>
        </div>

        {/* === Mon PayPal === */}
        <PayPalCard
          stats={stats}
          token={token}
          onSuccess={fetchStats}
        />

        {/* === Réclamer un paiement === */}
        <PayoutClaimCard
          stats={stats}
          token={token}
          onSuccess={fetchStats}
        />

        {/* Performance Overview */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 md:p-6 mb-4 md:mb-6">
          <h3 className="text-white font-semibold mb-3 md:mb-4 flex items-center gap-2 text-sm md:text-base">
            <BarChart3 className="w-4 h-4 md:w-5 md:h-5 text-purple-400" />
            Récapitulatif
          </h3>
          
          <div className="grid grid-cols-3 gap-2 md:gap-4">
            <div className="bg-black/30 rounded-lg p-3 md:p-4 text-center">
              <p className="text-gray-400 text-[10px] md:text-xs mb-1">Taux de commission</p>
              <p className="text-lg md:text-xl font-bold text-purple-400">{stats?.commission_rate || 10}%</p>
            </div>
            <div className="bg-black/30 rounded-lg p-3 md:p-4 text-center">
              <p className="text-gray-400 text-[10px] md:text-xs mb-1">Commission moyenne</p>
              <p className="text-lg md:text-xl font-bold text-cyan-400">
                {stats?.total_sales > 0 ? ((stats?.total_commission || 0) / stats.total_sales).toFixed(2) : '0.00'}€
              </p>
            </div>
            <div className="bg-black/30 rounded-lg p-3 md:p-4 text-center">
              <p className="text-gray-400 text-[10px] md:text-xs mb-1">Déjà payé</p>
              <p className="text-lg md:text-xl font-bold text-green-400">{(stats?.paid_commission || 0).toFixed(2)}€</p>
            </div>
          </div>
        </div>

        {/* Payment History (commissions payées par l'admin) */}
        {stats?.payment_history && stats.payment_history.length > 0 && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-400" />
              Historique des paiements
            </h3>
            <div className="space-y-3">
              {stats.payment_history.map((p, idx) => (
                <div
                  key={p.id || idx}
                  className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 py-3 border-b border-zinc-800 last:border-0"
                  data-testid={`payment-history-row-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center shrink-0">
                      <Check className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">
                        Paiement reçu
                      </p>
                      <p className="text-gray-500 text-xs">
                        {p.date ? new Date(p.date).toLocaleString('fr-FR') : 'Date inconnue'}
                        {p.paid_by ? ` • par ${p.paid_by}` : ''}
                      </p>
                      {p.proof_note && (
                        <p className="text-gray-400 text-xs mt-1 italic">« {p.proof_note} »</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 md:justify-end">
                    <p className="text-green-400 font-bold whitespace-nowrap">
                      +{(p.amount || 0).toFixed(2)}€
                    </p>
                    {p.proof_id ? (
                      <button
                        onClick={() => openProof(p.proof_id)}
                        disabled={proofLoadingId === p.proof_id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-cyan-600/20 hover:bg-cyan-600 text-cyan-300 hover:text-white transition-colors disabled:opacity-50"
                        data-testid={`view-proof-btn-${idx}`}
                      >
                        {proofLoadingId === p.proof_id ? (
                          <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <ImageIcon className="w-3.5 h-3.5" />
                        )}
                        Voir la preuve
                      </button>
                    ) : (
                      <span className="text-gray-600 text-xs italic">Sans preuve</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Sales */}
        {stats?.recent_sales && stats.recent_sales.length > 0 && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Gift className="w-5 h-5 text-cyan-400" />
              Dernières Ventes
            </h3>
            
            <div className="space-y-3">
              {stats.recent_sales.map((sale, index) => (
                <div key={index} className="flex items-center justify-between py-3 border-b border-zinc-800 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                      <ShoppingBag className="w-4 h-4 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Vente #{index + 1}</p>
                      <p className="text-gray-500 text-xs">
                        {sale.date ? new Date(sale.date).toLocaleDateString('fr-FR') : 'Date inconnue'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-green-400 font-semibold">+{(sale.commission || 0).toFixed(2)}€</p>
                    <p className="text-gray-500 text-xs">commission</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state for no sales */}
        {(!stats?.recent_sales || stats.recent_sales.length === 0) && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-8 text-center">
            <ShoppingBag className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">Pas encore de ventes</h3>
            <p className="text-gray-400 text-sm max-w-md mx-auto">
              Partage ton code <span className="text-purple-400 font-mono">{stats?.code}</span> avec ta communauté pour commencer à gagner des commissions !
            </p>
          </div>
        )}

      </div>

      {/* Proof viewer modal */}
      {proofViewer && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setProofViewer(null)}
          data-testid="proof-viewer-modal"
        >
          <div
            className="relative max-w-3xl w-full bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <div>
                <p className="text-white font-semibold text-sm">Preuve de paiement</p>
                {proofViewer.created_at && (
                  <p className="text-gray-500 text-xs">
                    {new Date(proofViewer.created_at).toLocaleString('fr-FR')}
                    {proofViewer.uploaded_by ? ` • par ${proofViewer.uploaded_by}` : ''}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setProofViewer(null)}
                className="text-gray-400 hover:text-white"
                data-testid="proof-viewer-close-btn"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="bg-black">
              <img
                src={`data:${proofViewer.content_type || 'image/png'};base64,${proofViewer.image_base64}`}
                alt="Preuve de paiement"
                className="w-full max-h-[75vh] object-contain"
                data-testid="proof-viewer-image"
              />
            </div>
            {proofViewer.note && (
              <div className="p-4 border-t border-zinc-800 text-sm text-gray-300 flex gap-2">
                <FileText className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                <span>{proofViewer.note}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAffiliatePage;
